import { Link, usePage } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar"
import type { NavItem } from "@/types"

// Cap the chip at 99+ — same convention as the creator-shell header
// trigger + OKS-36 bell badge. The shared-prop count is server-capped
// at 99 (`Conversations::UnreadCount::MAX_COUNT`) so this is purely
// cosmetic for the rendered string.
const CHIP_CAP_DISPLAY = "99+"
const CHIP_CAP = 99

export function NavMain({ items = [] }: { items: NavItem[] }) {
  const page = usePage()
  const { t } = useTranslation()
  return (
    <SidebarGroup className="px-2 py-0">
      <SidebarGroupLabel>{t("nav.group_platform")}</SidebarGroupLabel>
      <SidebarMenu>
        {items.map((item) => {
          const showUnread =
            typeof item.unreadCount === "number" && item.unreadCount > 0
          const chipText = showUnread
            ? item.unreadCount! > CHIP_CAP
              ? CHIP_CAP_DISPLAY
              : String(item.unreadCount)
            : null
          return (
            <SidebarMenuItem key={item.title}>
              <SidebarMenuButton
                asChild
                isActive={page.url.startsWith(item.href)}
                tooltip={{ children: item.title }}
              >
                <Link
                  href={item.href}
                  prefetch
                  data-testid={item["data-testid"]}
                >
                  {item.icon && <item.icon />}
                  <span className="flex-1">{item.title}</span>
                  {chipText && (
                    <Badge
                      variant="secondary"
                      className="ml-auto h-5 min-w-5 px-1.5 text-[10px]"
                      aria-label={item.unreadAriaLabel}
                      data-testid={
                        item["data-testid"]
                          ? `${item["data-testid"]}-unread`
                          : undefined
                      }
                    >
                      {chipText}
                    </Badge>
                  )}
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )
        })}
      </SidebarMenu>
    </SidebarGroup>
  )
}
